/**
 * "Sign in with <provider>" — starts SP-initiated SSO.
 *
 * Everything shown here comes from the discovery response: the provider name,
 * and the absolute URL to navigate to. No organisation ID or environment URL
 * is baked into the frontend, so one build works everywhere.
 *
 * This is a full-page navigation, not an XHR: the browser has to leave our
 * origin so the identity provider can run its own login, MFA and device
 * checks. A fetch would be blocked by CORS and would defeat the point of
 * never handling the password ourselves.
 */
import React from 'react';

/** Only Microsoft has a mark worth drawing; others fall back to a generic lock. */
function ProviderIcon({ provider }) {
  if (provider === 'ENTRA') {
    return (
      <svg className="sso_login_icon" width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#F25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
        <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
      </svg>
    );
  }
  return (
    <svg className="sso_login_icon" width="18" height="18" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/**
 * onBeforeNavigate lets a caller record where the round trip started before the
 * browser leaves our origin -- the audit portal uses it. Optional, so the main
 * sign-in screen is unaffected.
 */
function SsoLoginButton({ sso, isChecking, disabled, onBeforeNavigate }) {
  if (isChecking) {
    return (
      <p className="sso_login_checking">Checking your organisation…</p>
    );
  }

  if (!sso?.ssoEnabled || !sso?.loginUrl) return null;

  const label = sso.providerLabel || 'your organisation';

  return (
    <div className="sso_login_block">
      {!sso.enforceSso && (
        <div className="sso_divider">
          <span>or</span>
        </div>
      )}

      <button
        type="button"
        className="sso_login_btn"
        onClick={() => {
          if (onBeforeNavigate) onBeforeNavigate();
          window.location.assign(sso.loginUrl);
        }}
        disabled={disabled}
      >
        <ProviderIcon provider={sso.provider} />
        Sign in with {label}
      </button>

      <p className="sso_login_hint">
        {sso.enforceSso
          ? 'Your organisation requires single sign-on.'
          : 'Use your work account. Your password is never entered here.'}
      </p>
    </div>
  );
}

export default SsoLoginButton;
